import { ChoosePeriodModal } from "@/components/date-filter/ChoosePeriodModal.tsx";
import {
  buildPresetGroups,
  PeriodPresetOption,
  useDateFilterFormat,
} from "@/components/date-filter/date-filter-presets.ts";
import { isDateFilterEmpty } from "@/components/date-filter/date-filter.utils.ts";
import {
  ApiDateFilter,
  ApiDateFilterMode,
  ApiDateFilterOperator,
  ApiDateFilterPreset,
} from "@/components/date-filter/date-filter.type.ts";
import {
  Select,
  SelectItem,
  SelectOptionItem,
  SelectValueType,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface PeriodFilterProps {
  title: string;
  value: ApiDateFilter | undefined;
  onChange: (value: ApiDateFilter | undefined) => void;
  availableModes?: ApiDateFilterMode[];
}

export function PeriodFilter({ title, value, onChange, availableModes }: PeriodFilterProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { formatRange } = useDateFilterFormat();

  const modes = availableModes ?? [ApiDateFilterMode.date, ApiDateFilterMode.day];
  const { exact, annualThis, annualFuture, annualPast } = buildPresetGroups(modes);

  const hasValue = !isDateFilterEmpty(value);
  const isCustom = value?.operator === ApiDateFilterOperator.custom;

  const presetId = (mode: ApiDateFilterMode, preset: ApiDateFilterPreset) => `${mode}:${preset}`;

  const exactOptions = exact.map((option) => ({
    id: presetId(option.mode, option.preset),
    title: t(`common:filter_preset_${option.preset}`),
    group: t("common:period_group_exact"),
  }));

  const annualOptions = (group: PeriodPresetOption[], groupLabel: string) =>
    group.map((option) => ({
      id: presetId(option.mode, option.preset),
      title: t(`common:filter_preset_annual_${option.preset}`),
      group: groupLabel,
    }));

  const options: SelectOptionItem[] = [
    ...exactOptions,
    ...annualOptions(annualThis, t("common:period_group_annual_this")),
    ...annualOptions(annualFuture, t("common:period_group_annual_future")),
    ...annualOptions(annualPast, t("common:period_group_annual_past")),
    { type: "divider" },
    {
      id: "custom",
      title: t("common:custom_period"),
      caption: t("common:custom_period_description"),
    },
  ];

  const selectedId: SelectValueType = !hasValue
    ? undefined
    : isCustom
      ? "custom"
      : presetId(value!.mode, value!.preset!);

  const openCustom = () =>
    openModal(
      ChoosePeriodModal,
      { value, availableModes: modes, onSave: onChange },
      { size: "md" }
    );

  const onSelect = (id: SelectValueType) => {
    if (id == null) {
      onChange(undefined);
      return;
    }
    if (id === "custom") {
      openCustom();
      return;
    }
    const [mode, preset] = String(id).split(":");
    onChange({
      mode: mode as ApiDateFilterMode,
      operator: ApiDateFilterOperator.preset,
      preset: preset as ApiDateFilterPreset,
      value_from: undefined,
      value_to: undefined,
    });
  };

  const renderSelectedTitle = (entities: SelectItem[]) => {
    const entity = entities[0];
    if (!entity) return null;
    if (entity.id === "custom") return formatRange(value) ?? entity.title;
    return entity.title;
  };

  return (
    <Select
      label={title}
      emptyTitle={t("common:select")}
      isClearable
      showCaption
      captionPerItem
      value={selectedId}
      options={options}
      onChange={onSelect}
      renderSelectedTitle={renderSelectedTitle}
    />
  );
}
